import { FrameworkTemplate, ItemStatus } from './types';

export const DEFAULT_FRAMEWORK_TEMPLATE: FrameworkTemplate = {
  id: "default-framework",
  name: "Platform Development Framework",
  description: "A comprehensive framework for capturing requirements, architecting solutions, and implementing platforms.",
  sections: [
    {
      name: "1. Requirements & Planning",
      description: "Capturing business objectives, user needs, and market analysis",
      defaultWeight: 1.0,
      isCore: true,
      subsections: [
        {
          name: "1.1 Vision & Goals",
          description: "Define the project's purpose, objectives, and success metrics",
          defaultWeight: 1.0,
          items: [
            {
              title: "Vision Statement",
              description: "Concise statement of the project's purpose and long-term goal",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Should clearly articulate what the platform aims to achieve and why it matters."
            },
            {
              title: "Business Objectives",
              description: "Clear, measurable business goals that the platform will help achieve",
              defaultWeight: 0.8,
              isRequired: true,
              guidance: "Objectives should be SMART (Specific, Measurable, Achievable, Relevant, Time-bound)."
            },
            {
              title: "Success Metrics",
              description: "Measurable indicators that will determine the success of the project",
              defaultWeight: 0.7,
              isRequired: true,
              guidance: "Define KPIs that align with business objectives and can be tracked over time."
            },
            {
              title: "Strategic Alignment",
              description: "How this project aligns with broader organizational goals",
              defaultWeight: 0.5,
              isRequired: false,
              guidance: "Explain how this project supports the company's strategy and vision."
            }
          ]
        },
        {
          name: "1.2 User Analysis",
          description: "Define user personas, journeys, and pain points",
          defaultWeight: 1.0,
          items: [
            {
              title: "User Personas",
              description: "Detailed profiles of primary user types who will use the platform",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Include demographics, goals, pain points, technical proficiency, and behavioral patterns."
            },
            {
              title: "User Journeys",
              description: "Narrative description of how users interact with the platform",
              defaultWeight: 0.8,
              isRequired: true,
              guidance: "Map out key scenarios covering the entire user experience from first contact to becoming a power user."
            },
            {
              title: "Pain Points",
              description: "Current problems users face that this platform will solve",
              defaultWeight: 0.7,
              isRequired: true,
              guidance: "Identify frustrations, inefficiencies, and challenges users currently experience."
            }
          ]
        },
        {
          name: "1.3 Market Analysis",
          description: "Analyze competitors, market trends, and position the platform",
          defaultWeight: 0.8,
          items: [
            {
              title: "Competitive Analysis",
              description: "Analysis of similar solutions, their strengths and weaknesses",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Consider direct and indirect competitors, focusing on how your platform will differentiate."
            },
            {
              title: "Market Trends",
              description: "Current and emerging trends in the market space",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Identify technology trends, user expectation shifts, and industry directions."
            },
            {
              title: "Feature Comparison",
              description: "Comparison of features against competitors and market standards",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Create a matrix comparing your planned features against top competitors."
            }
          ]
        }
      ]
    },
    {
      name: "2. Functional Architecture",
      description: "Defining features, data models, and integration patterns",
      defaultWeight: 1.0,
      isCore: true,
      subsections: [
        {
          name: "2.1 Core Features",
          description: "Define the key features and capabilities of the platform",
          defaultWeight: 1.0,
          items: [
            {
              title: "Feature List",
              description: "Comprehensive list of features with prioritization",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Use the MoSCoW method (Must have, Should have, Could have, Won't have) for prioritization."
            },
            {
              title: "User Stories",
              description: "User-centric stories describing features from the user's perspective",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Format as 'As a [type of user], I want [goal] so that [benefit]'."
            },
            {
              title: "MVP Scope",
              description: "Defined scope for the Minimum Viable Product",
              defaultWeight: 0.8,
              isRequired: true,
              guidance: "Focus on core value delivery with the minimum feature set needed to launch."
            }
          ]
        },
        {
          name: "2.2 Data Architecture",
          description: "Define data entities, relationships, and management strategies",
          defaultWeight: 1.0,
          items: [
            {
              title: "Entity Model",
              description: "Definition of key data entities and their relationships",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Create an Entity-Relationship Diagram (ERD) showing how data objects relate to each other."
            },
            {
              title: "Data Flow Diagrams",
              description: "Visualization of how data moves through the system",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Show data sources, processing steps, storage, and consumption points."
            },
            {
              title: "Data Governance",
              description: "Policies for data quality, privacy, and lifecycle management",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define data ownership, retention policies, and compliance requirements."
            }
          ]
        },
        {
          name: "2.3 Integration Framework",
          description: "Define integration points, APIs, and external systems",
          defaultWeight: 0.9,
          items: [
            {
              title: "Integration Inventory",
              description: "List of systems to integrate with",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document all external systems, the data exchanged, and integration purpose."
            },
            {
              title: "API Contracts",
              description: "Detailed specification of APIs using standard formats",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Use OpenAPI/Swagger format to document endpoints, parameters, and responses."
            },
            {
              title: "Integration Patterns",
              description: "Chosen integration patterns and strategies",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Select from patterns like request/response, event-driven, batch processing, etc."
            }
          ]
        }
      ]
    },
    {
      name: "3. Technical Architecture",
      description: "Designing application architecture, APIs, and frontend solutions",
      defaultWeight: 1.0,
      isCore: true,
      subsections: [
        {
          name: "3.1 Application Architecture",
          description: "Define the overall architecture style and approach",
          defaultWeight: 1.0,
          items: [
            {
              title: "Architecture Overview",
              description: "High-level architecture diagram and description",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Visualize major components, their relationships, and communication patterns."
            },
            {
              title: "Technology Stack",
              description: "Selected technologies for different layers of the application",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Specify frontend, backend, database, and infrastructure technologies with rationale."
            },
            {
              title: "Architecture Decisions",
              description: "Key architectural decisions with rationale",
              defaultWeight: 0.8,
              isRequired: true,
              guidance: "Document important decisions using Architecture Decision Records (ADRs)."
            }
          ]
        },
        {
          name: "3.2 Microservices Strategy",
          description: "Define the approach to service design and boundaries",
          defaultWeight: 0.7,
          items: [
            {
              title: "Service Boundaries",
              description: "Definition of service boundaries and responsibilities",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Use Domain-Driven Design principles to identify bounded contexts and service boundaries."
            },
            {
              title: "Communication Patterns",
              description: "Patterns for inter-service communication",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Decide between synchronous (REST, gRPC) and asynchronous (events, queues) communication."
            },
            {
              title: "Data Consistency",
              description: "Strategy for maintaining data consistency across services",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Consider eventual consistency, SAGA patterns, and distributed transaction approaches."
            }
          ]
        },
        {
          name: "3.3 API Strategy",
          description: "Define API design standards and governance",
          defaultWeight: 0.8,
          items: [
            {
              title: "API Design Standards",
              description: "Standards and patterns for API design",
              defaultWeight: 0.8,
              isRequired: false,
              guidance: "Define naming conventions, resource structure, error handling, and versioning approach."
            },
            {
              title: "API Versioning",
              description: "Strategy for API versioning and evolution",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Determine whether to use URI path versioning, header versioning, or another approach."
            },
            {
              title: "API Security",
              description: "Security controls for API access and usage",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Define authentication methods, authorization rules, and additional security measures."
            }
          ]
        },
        {
          name: "3.4 Frontend Architecture",
          description: "Define the approach to frontend development",
          defaultWeight: 0.9,
          items: [
            {
              title: "Component Library",
              description: "Reusable UI component strategy and documentation",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document the component hierarchy, reuse strategy, and component documentation approach."
            },
            {
              title: "State Management",
              description: "Approach to managing application state",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Select appropriate state management patterns and libraries based on application complexity."
            },
            {
              title: "Frontend Performance",
              description: "Strategies for optimizing frontend performance",
              defaultWeight: 0.8,
              isRequired: false,
              guidance: "Address bundle size, lazy loading, rendering optimization, and caching strategies."
            }
          ]
        }
      ]
    },
    {
      name: "4. Cross-Cutting Concerns",
      description: "Addressing security, scalability, localization, and other system-wide considerations",
      defaultWeight: 0.9,
      isCore: true,
      subsections: [
        {
          name: "4.1 DevOps & CI/CD",
          description: "Define continuous integration and delivery approach",
          defaultWeight: 0.8,
          items: [
            {
              title: "CI/CD Pipeline",
              description: "Continuous integration and delivery pipeline configuration",
              defaultWeight: 0.8,
              isRequired: false,
              guidance: "Define build, test, and deployment stages with appropriate automation."
            },
            {
              title: "Environment Strategy",
              description: "Development, testing, staging, and production environments",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document environment configurations, promotion criteria, and management approach."
            },
            {
              title: "Monitoring Strategy",
              description: "Approach to monitoring application health and performance",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Select monitoring tools, define key metrics, and establish alerting thresholds."
            }
          ]
        },
        {
          name: "4.2 Scalability & Performance",
          description: "Define strategies for scaling and performance optimization",
          defaultWeight: 0.8,
          items: [
            {
              title: "Scaling Requirements",
              description: "Expected scale and growth projections",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document current and projected user counts, transaction volumes, and data growth."
            },
            {
              title: "Caching Strategy",
              description: "Multi-level caching approach for performance",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define caching at CDN, application, and database levels with invalidation strategies."
            },
            {
              title: "Performance Budget",
              description: "Performance targets and constraints",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Set specific performance metrics like page load time, response time, and resource utilization."
            }
          ]
        },
        {
          name: "4.3 Security",
          description: "Define security requirements and controls",
          defaultWeight: 1.0,
          items: [
            {
              title: "Security Architecture",
              description: "Overall security approach and controls",
              defaultWeight: 1.0,
              isRequired: true,
              guidance: "Address authentication, authorization, data protection, and network security."
            },
            {
              title: "Authentication & Authorization",
              description: "User identity and access control approach",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Define authentication methods, role-based access control, and permission management."
            },
            {
              title: "Data Protection",
              description: "Strategy for protecting sensitive data",
              defaultWeight: 0.9,
              isRequired: true,
              guidance: "Address encryption (at rest, in transit), data masking, and secure storage practices."
            },
            {
              title: "Compliance Requirements",
              description: "Regulatory and compliance considerations",
              defaultWeight: 0.8,
              isRequired: false,
              guidance: "Document applicable regulations (GDPR, HIPAA, etc.) and compliance approach."
            }
          ]
        },
        {
          name: "4.4 Localization",
          description: "Define approach to supporting multiple languages and regions",
          defaultWeight: 0.6,
          items: [
            {
              title: "Localization Strategy",
              description: "Overall approach to localization",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define supported languages, translation process, and cultural adaptations."
            },
            {
              title: "Internationalization",
              description: "Technical implementation of i18n",
              defaultWeight: 0.5,
              isRequired: false,
              guidance: "Address date/time formatting, number formatting, and RTL support."
            }
          ]
        },
        {
          name: "4.5 Theming",
          description: "Define approach to visual theming and customization",
          defaultWeight: 0.5,
          items: [
            {
              title: "Theme Architecture",
              description: "Approach to theme implementation",
              defaultWeight: 0.5,
              isRequired: false,
              guidance: "Define theme structure, inheritance model, and customization capabilities."
            },
            {
              title: "Design Tokens",
              description: "System of design variables for consistent styling",
              defaultWeight: 0.4,
              isRequired: false,
              guidance: "Document color palettes, typography, spacing, and other design variables."
            }
          ]
        },
        {
          name: "4.6 AI Capabilities",
          description: "Define AI features and integration approach",
          defaultWeight: 0.7,
          items: [
            {
              title: "AI Capability Framework",
              description: "Overview of AI capabilities and their purpose",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document AI features, their business value, and implementation approach."
            },
            {
              title: "Model Selection",
              description: "AI models to be used and their selection criteria",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Identify specific models, whether to use third-party APIs or custom models."
            },
            {
              title: "Ethical AI Guidelines",
              description: "Principles for responsible AI use",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Address bias, fairness, transparency, and user privacy in AI implementations."
            }
          ]
        }
      ]
    },
    {
      name: "5. Implementation Strategy",
      description: "Planning development methodology, phased delivery, and operations",
      defaultWeight: 0.8,
      isCore: true,
      subsections: [
        {
          name: "5.1 Development Methodology",
          description: "Define the development process and team structure",
          defaultWeight: 0.7,
          items: [
            {
              title: "Development Process",
              description: "Selected development methodology and practices",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Specify Agile approach (Scrum, Kanban), sprint cadence, and key ceremonies."
            },
            {
              title: "Team Structure",
              description: "Team organization and roles",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define team composition, responsibilities, and collaboration model."
            },
            {
              title: "Quality Assurance",
              description: "Approach to ensuring software quality",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document testing strategy, QA processes, and quality gates."
            }
          ]
        },
        {
          name: "5.2 Phased Delivery",
          description: "Define the approach to incremental delivery",
          defaultWeight: 0.8,
          items: [
            {
              title: "Release Roadmap",
              description: "Phased release plan and timeline",
              defaultWeight: 0.8,
              isRequired: false,
              guidance: "Create a high-level roadmap showing major releases and key milestones."
            },
            {
              title: "Feature Flagging",
              description: "Strategy for progressive feature rollout",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define approach to feature flags for controlled rollout and A/B testing."
            },
            {
              title: "Beta Program",
              description: "Approach to early user testing",
              defaultWeight: 0.5,
              isRequired: false,
              guidance: "Document beta user selection, feedback collection, and iteration process."
            }
          ]
        },
        {
          name: "5.3 Operations",
          description: "Define ongoing operational processes",
          defaultWeight: 0.7,
          items: [
            {
              title: "Support Model",
              description: "Customer and technical support approach",
              defaultWeight: 0.6,
              isRequired: false,
              guidance: "Define support levels, SLAs, and issue resolution process."
            },
            {
              title: "Incident Response",
              description: "Process for handling production incidents",
              defaultWeight: 0.7,
              isRequired: false,
              guidance: "Document incident classification, response procedures, and post-mortem process."
            },
            {
              title: "Maintenance Schedule",
              description: "Plan for routine maintenance and updates",
              defaultWeight: 0.5,
              isRequired: false,
              guidance: "Define update cadence, maintenance windows, and version policy."
            }
          ]
        }
      ]
    }
  ]
};

// Function to create a new project from the template
export const createProjectFromTemplate = (
  name: string,
  description: string,
  template: FrameworkTemplate = DEFAULT_FRAMEWORK_TEMPLATE
) => {
  const now = new Date();
  return {
    id: crypto.randomUUID(),
    name,
    description,
    createdAt: now,
    updatedAt: now,
    sections: template.sections.map(sectionTemplate => ({
      id: crypto.randomUUID(),
      name: sectionTemplate.name,
      description: sectionTemplate.description,
      weight: sectionTemplate.defaultWeight,
      isCore: sectionTemplate.isCore,
      completionPercentage: 0,
      isExpanded: false,
      subsections: sectionTemplate.subsections.map(subsectionTemplate => ({
        id: crypto.randomUUID(),
        name: subsectionTemplate.name,
        description: subsectionTemplate.description,
        weight: subsectionTemplate.defaultWeight,
        completionPercentage: 0,
        isExpanded: false,
        items: subsectionTemplate.items.map(itemTemplate => ({
          id: crypto.randomUUID(),
          title: itemTemplate.title,
          description: itemTemplate.description,
          status: ItemStatus.NotStarted,
          weight: itemTemplate.defaultWeight,
          isRequired: itemTemplate.isRequired,
          lastModified: now,
          notes: itemTemplate.guidance
        }))
      }))
    })),
    overallProgress: 0
  };
};
